import React, { FC, useMemo } from 'react';
import { useProduct } from '$shared/utils/product';

const materialColorMap: Record<string, string> = {
    P: '#d8ecf8',
    M: '#fff0aa',
    K: '#f2b8b8',
    N: '#cfe8c4',
    S: '#ded5c8',
    H: '#d3d3d3',
    O: '#e5e5e5',
};

export const PdpCuttingData: FC = () => {
    const { cuttingData } = useProduct();

    const cuttingConditions = useMemo(() => {
        if (!cuttingData?.data) {
            return { columns: [], rows: [] };
        }

        try {
            const cleanValue = (value: any): string => {
                if (value == null) return '-';

                const stringValue = String(value).trim();
                if (
                    stringValue.includes('{=IF(') ||
                    stringValue.includes('{=ROUND(') ||
                    stringValue.includes('{=CALC(') ||
                    stringValue.includes('{=ATTRVAL(')
                ) {
                    return '-';
                }

                return stringValue;
            };

            const parsed =
                typeof cuttingData.data === 'string'
                    ? JSON.parse(cuttingData.data)
                    : cuttingData.data;

            const visibleColumns = [
                { key: 'Applicable machined materials', label: 'Material' },
                { key: 'Vc_core (m/min)', label: 'Vc (m/min)' },
                { key: 'f_core (mm)', label: 'f (mm/rev)' },
                { key: 'ap_core (mm)', label: 'ap (mm)' },
            ];

            const rawRows =
                parsed?.Table?.Rows ??
                parsed?.table?.Rows ??
                parsed?.rows ??
                {};

            const rows = Array.isArray(rawRows)
                ? rawRows
                : Object.entries(rawRows).map(
                    ([externalKey, attributesObject]: any) => ({
                        ExternalKey: externalKey,
                        CuttingCondition: attributesObject?.cuttingCondition,
                        Attributes: Object.entries(attributesObject || {})
                            .filter(
                                ([attributeName]) =>
                                    attributeName !== 'cuttingCondition'
                            )
                            .map(([attributeName, attribute]: any) => ({
                                Name: attributeName,
                                Label: attributeName,
                                TranslatedName: attributeName,
                                Value:
                                    attribute?.FormattedValue ||
                                    attribute?.formattedValue ||
                                    attribute?.Value ||
                                    attribute?.value ||
                                    '',
                            })),
                    })
                );

            if (!rows.length) {
                return { columns: [], rows: [] };
            }

            const formattedRows = rows.map((row: any) => {
                const formatted: Record<string, any> = {};

                const rowAttributes = row.Attributes ?? row.attributes ?? [];

                rowAttributes.forEach((attr: any) => {
                    const key =
                        attr.TranslatedName ||
                        attr.translatedName ||
                        attr.Label ||
                        attr.label ||
                        attr.Name ||
                        attr.name;

                    formatted[key] = cleanValue(
                        attr.FormattedValue ||
                        attr.formattedValue ||
                        attr.Value ||
                        attr.value
                    );
                });

                formatted.cuttingCondition =
                    row.CuttingCondition ?? row.cuttingCondition;

                return formatted;
            });

            return {
                columns: visibleColumns,
                rows: formattedRows,
            };
        } catch (error) {
            console.error('Failed parsing cutting data', error);

            return { columns: [], rows: [] };
        }
    }, [cuttingData]);

    if (!cuttingConditions.rows.length) {
        return null;
    }

    return (
        <div className="pdp-cutting-data">
            <table className="pdp-cutting-data__table">
                <thead>
                <tr>
                    <th>
                        MATERIAL MAIN<br />GROUP
                    </th>
                    <th>MATERIAL CODE</th>
                    <th>Vc</th>
                    <th>ap</th>
                    <th>f</th>
                    <th>
                        WORKPIECE MATERIAL<br />MAIN GROUP
                    </th>
                    <th>
                        STRUCTURE /<br />COMPOSITIONS
                    </th>
                    <th>HARDNESS</th>
                </tr>
                </thead>

                <tbody>
                {cuttingConditions.rows.map((row: any, index: number) => {
                    const material =
                        row['Applicable machined materials'] || '-';
                    const materialGroup = ['P14', 'P15', 'P16'].includes(
                        material
                    )
                        ? 'M'
                        : material.charAt(0);

                    const cuttingConditionValues =
                        row.cuttingCondition?.cellValues ?? [];

                    return (
                        <tr key={`${material}-${index}`}>
                            <td>
                                <div
                                    className="pdp-cutting-data__material-circle"
                                    style={{
                                        backgroundColor:
                                            materialColorMap[
                                                materialGroup
                                                ] || '#ccc',
                                        color: '#333',
                                        border: 'none',
                                    }}
                                >
                                    {materialGroup}
                                </div>
                            </td>

                            <td data-label="Material code">{material}</td>

                            <td data-label="Vc">
                                {row['Vc_core (m/min)'] || '-'} m/min
                            </td>

                            <td data-label="ap">
                                {row['ap_core (mm)']
                                    ? Number(row['ap_core (mm)']).toFixed(3)
                                    : '-'}{' '}
                                mm
                            </td>

                            <td data-label="f">
                                {row['f_core (mm)']
                                    ? Number(row['f_core (mm)']).toFixed(3)
                                    : '-'}{' '}
                                mm/rev
                            </td>

                            <td data-label="Workpiece material main group">
                                {cuttingConditionValues[3] || '-'}
                            </td>

                            <td data-label="Structure / compositions">
                                {cuttingConditionValues[4] || '-'}
                            </td>

                            <td data-label="Hardness">
                                {cuttingConditionValues[5]
                                    ? `${cuttingConditionValues[5]} ${
                                        cuttingConditionValues[6] || ''
                                    }`.trim()
                                    : '-'}
                            </td>
                        </tr>
                    );
                })}
                </tbody>
            </table>

            <style jsx>{`
                .pdp-cutting-data {
                    margin-top: 24px;
                    width: 100%;
                    max-width: 100%;
                    overflow-x: hidden;
                    font-family: inherit;
                    color: #5f5f5f;
                }

                .pdp-cutting-data__table {
                    width: 100%;
                    max-width: 100%;
                    border-collapse: collapse;
                    table-layout: fixed;
                    font-size: 14px;
                    color: #5f5f5f;
                }

                .pdp-cutting-data__table th {
                    text-align: center;
                    padding: 14px 12px;
                    font-family: inherit;
                    font-size: 14px;
                    font-weight: 400;
                    color: #555;
                    text-transform: none;
                    border-bottom: 1px solid #e5e5e5;
                    line-height: 1.25;
                }

                .pdp-cutting-data__table td {
                    padding: 12px;
                    text-align: center;
                    vertical-align: middle;
                    font-weight: 400;
                    color: #666;
                    line-height: 1.35;
                    word-break: normal;
                }

                .pdp-cutting-data__table tbody tr:nth-child(odd) {
                    background: #f4f4f4;
                }

                .pdp-cutting-data__table tbody tr:nth-child(even) {
                    background: #fff;
                }

                .pdp-cutting-data__material-circle {
                    width: 48px;
                    height: 48px;
                    margin: 0 auto;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-family: inherit;
                    font-size: 20px;
                    font-weight: 600;
                    line-height: 1;
                }

                @media (max-width: 767px) {
                    .pdp-cutting-data__table,
                    .pdp-cutting-data__table tbody,
                    .pdp-cutting-data__table tr,
                    .pdp-cutting-data__table td {
                        display: block;
                        width: 100%;
                    }

                    .pdp-cutting-data__table thead {
                        display: none;
                    }

                    .pdp-cutting-data__table tr {
                        box-sizing: border-box;
                        display: grid;
                        grid-template-columns: 56px minmax(0, 1fr);
                        column-gap: 12px;
                        row-gap: 8px;
                        padding: 12px;
                        margin-bottom: 12px;
                        border: 1px solid #e5e5e5;
                    }

                    .pdp-cutting-data__table td {
                        box-sizing: border-box;
                        padding: 0;
                        text-align: left;
                    }

                    .pdp-cutting-data__table td:first-child {
                        grid-row: 1 / span 7;
                        align-self: center;
                    }

                    .pdp-cutting-data__material-circle {
                        width: 42px;
                        height: 42px;
                        margin: 0;
                    }

                    .pdp-cutting-data__table td:not(:first-child) {
                        display: grid;
                        grid-template-columns: 110px minmax(0, 1fr);
                        gap: 10px;
                        align-items: baseline;
                    }

                    .pdp-cutting-data__table td:not(:first-child)::before {
                        content: attr(data-label);
                        color: #777;
                        font-weight: 400;
                    }
                }
            `}</style>
        </div>
    );
};